import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import type { NavItem } from "./navConfig";
import { useNavLabel } from "./useNavLabel";

interface Props {
  item: NavItem;
  isActive: boolean;
  badge?: number;
  onClick: () => void;
  /**
   * Renders with the same weight as a section header row. For sections that collapse into a single
   * plain row (Overview, Assistant) — two rows on the same level must not look like two levels.
   */
  topLevel?: boolean;
}

const BADGE_SIZES = {
  md: { box: "20px", font: "11px", px: "1.5" },
  sm: { box: "18px", font: "10px", px: "1" },
};

export const NavBadgeCount = ({
  value,
  isActive,
  size = "md",
}: {
  value: number;
  isActive?: boolean;
  size?: keyof typeof BADGE_SIZES;
}) => {
  const { box, font, px } = BADGE_SIZES[size];
  return (
    <Box
      flexShrink={0}
      minW={box}
      h={box}
      px={px}
      rounded="full"
      fontSize={font}
      fontWeight="semibold"
      lineHeight={box}
      textAlign="center"
      bg={isActive ? "smPrimary" : "smBackgroundTertiary"}
      color={isActive ? "white" : "smText"}
    >
      {value > 99 ? "99+" : value}
    </Box>
  );
};

export const SidebarNavItem = ({ item, isActive, badge, onClick, topLevel }: Props) => {
  const navLabel = useNavLabel();

  return (
    <Flex
      as="button"
      type="button"
      onClick={onClick}
      align="center"
      gap="3"
      w="full"
      // Indented under its section header, so a submenu row reads as one level down. A section that
      // renders as a single plain row is not under anything and keeps the header's own inset.
      pl={topLevel ? "3" : "8"}
      pr="3"
      py="2"
      mb="0.5"
      rounded="lg"
      textAlign="left"
      transition="background-color 0.15s, color 0.15s"
      bg={isActive ? "smPrimarySofter" : "transparent"}
      color={isActive ? "smPrimary" : topLevel ? "smText" : "smMuted"}
      fontWeight={isActive || topLevel ? "medium" : "normal"}
      fontSize="sm"
      _hover={{ bg: isActive ? "smPrimarySofter" : "smBackgroundSecondary" }}
      // Chakra's Collapse hard-codes overflow:hidden inline on the submenu, and the aside clips
      // too, so a ring drawn outside the row would be cut on every side. This one is inside it.
      _focusVisible={{
        boxShadow: "smInsetFocusRing",
        outline: "none",
      }}
    >
      <Icon as={item.icon} boxSize="18px" flexShrink={0} />
      <Text flex="1" isTruncated title={navLabel(item.labelKey)}>
        {navLabel(item.labelKey)}
      </Text>
      {badge != null && badge > 0 && <NavBadgeCount value={badge} isActive={isActive} />}
    </Flex>
  );
};
